import { connect } from 'react-redux';
import { isEmpty } from 'lodash';

// Utils
import { length } from '../../../../common/utils/immutableHelper';
import { isTable } from '../../../../common/elements/utils/elementTypeUtils';
import {
    applyInitialCellValues,
    createTableContentDataFromValues,
} from '../../../../common/table/utils/tableInitialisationUtils';
import { showFileInDetailView } from '../../../../common/files/filePreviewUtils';
import HotTableClipboardManager from '../../../element/table/manager/HotTableClipboardManager';
import { convertDataToReduxFormat } from '../../../element/table/utils/tableDataUtils';
import { isUrlFile, readLinkDataFromFile } from '../../../../common/files/urlFileParsers';
import { getColumnCount } from '../../../../common/table/utils/tableCellDataPropertyUtils';
import { createCardJsonContent } from '../../../../common/tiptap/utils/createJsonContentUtils/createJsonContent';

// Selectors
import { getSelectedElements } from '../../../element/selection/selectedElementsSelector';
import { getCurrentlyEditingId } from '../../../element/selection/currentlyEditingSelector';
import { getClipboardSaveId } from './clipboardSelectors';
import { getCurrentModalElementId } from '../../../element/modal/elementModalCommonSelector';
import { getPageIdSelector } from '../../../reducers/sessionSelector';
import {
    getIsDoubleClickToCreateNotesSettingEnabled,
    userLanguagePreferenceSelector,
} from '../../../user/currentUserSelector';
import getGridSize from '../../../utils/grid/gridSizeSelector';

// Action creators
import {
    createAndEditElement,
    createElementSync,
    updateSelectedElements,
} from '../../../element/actions/elementActions';
import { deselectAllElements } from '../../../element/selection/selectionActions';
import { inferIsFileCompatibleWithImageElement } from '../../../../common/files/fileValidator';
import { pasteElements, saveToClipboard } from './clipboardActions';
import { setLinkElementUrl } from '../../../element/link/linkActions';
import { setElementLocalData } from '../../../element/local/elementLocalDataActions';

// Constants
import { ELEMENT_DISPLAY_MODES } from '../../../../common/elements/elementDisplayModeConstants';
import { ELEMENT_CREATION_SOURCES } from '../../../../common/elements/elementConstants';
import { TABLE_DEFAULT_COL_WIDTH } from '../../../../common/table/tableConstants';
import { ElementType } from '../../../../common/elements/elementTypes';
import { isValidColorFormat } from '../../../../common/colors/colorSpaceUtil';
import {
    getSwatchPropertiesFromText,
    INITIAL_SWATCH_PROPERTIES,
} from '../../../../common/tiptap/extensions/universalCard/swatch';

const mapDispatchToProps = (dispatch) => {
    const deselectAll = () => dispatch(deselectAllElements());

    const dispatchGetSelectedElements = () => dispatch((_, getState) => getSelectedElements(getState()));

    const dispatchGetCurrentlyEditingId = () => dispatch((_, getState) => getCurrentlyEditingId(getState()));

    const dispatchGetPageId = () => dispatch((_, getState) => getPageIdSelector(getState()));

    const dispatchGetCurrentModalElementId = () => dispatch((_, getState) => getCurrentModalElementId(getState()));

    const dispatchGetClipboardSaveId = () => dispatch((_, getState) => getClipboardSaveId(getState()));

    const dispatchSaveToClipBoard = (args) => dispatch(saveToClipboard(args));

    const dispatchPasteElements = (elements, sessionId) => dispatch(pasteElements({ elements, sessionId }));

    const createShortcutElement = ({
        elementType,
        location,
        content,
        attachment,
        currentBoardId,
        creationSource,
        select = true,
        edit = true,
    }) => {
        // Default the current board ID to the drop location if not provided, to simplify the API
        const _currentBoardId = currentBoardId || location.parentId;

        return dispatch(
            createAndEditElement({
                elementType,
                location,
                attachment,
                content,
                currentBoardId: _currentBoardId,
                creationSource,
                select,
                edit,
            }),
        );
    };

    const createShortcutColorSwatch = ({ location, currentBoardId, text, creationSource }) => {
        const elementConfig = {
            elementType: ElementType.COLOR_SWATCH_TYPE,
            location,
            currentBoardId,
            creationSource,
            content: {
                ...INITIAL_SWATCH_PROPERTIES,
                ...getSwatchPropertiesFromText(text),
            },
            edit: false,
            select: false,
        };

        return createShortcutElement(elementConfig);
    };

    const createShortcutCard = ({ location, currentBoardId, textContent, text, creationSource }) =>
        dispatch((_, getState) => {
            const state = getState();
            const isDoubleClickToCreateNotesSettingEnabled = getIsDoubleClickToCreateNotesSettingEnabled(state);

            if (!isDoubleClickToCreateNotesSettingEnabled && creationSource === ELEMENT_CREATION_SOURCES.DOUBLE_CLICK)
                return;

            if (isValidColorFormat(text)) {
                return createShortcutColorSwatch({ location, currentBoardId, text, creationSource });
            }

            const elementConfig = {
                elementType: ElementType.CARD_TYPE,
                location,
                currentBoardId,
                creationSource,
            };

            let convertedTextContent = null;

            if (text) {
                convertedTextContent = createCardJsonContent(null, text);
            }

            const _textContent = textContent || convertedTextContent;

            if (_textContent) {
                elementConfig.content = { textContent: _textContent };
            }

            return createShortcutElement(elementConfig);
        });

    const createShortcutFile = async ({
        location,
        currentBoardId,
        file,
        caption,
        creationSource,
        showCaption = true,
    }) => {
        const content = caption
            ? {
                  showCaption,
                  caption,
              }
            : {};
        const elementConfigBase = {
            location,
            currentBoardId,
            creationSource,
            select: false,
            edit: false,
            content,
        };

        // Turn dropped url files into Link elements
        if (isUrlFile(file)) {
            const { url, error } = await readLinkDataFromFile(file);
            return createShortcutLink({
                ...elementConfigBase,
                url,
                error,
            });
        }

        // Turn dropped images into Image elements
        if (await inferIsFileCompatibleWithImageElement(file)) {
            return createShortcutElement({
                ...elementConfigBase,
                elementType: ElementType.IMAGE_TYPE,
                attachment: file,
            });
        }

        // Turn any other filetype into a File element
        const displayMode = showFileInDetailView(file)
            ? ELEMENT_DISPLAY_MODES.DETAIL_VIEW
            : ELEMENT_DISPLAY_MODES.ICON_VIEW;
        return createShortcutElement({
            ...elementConfigBase,
            elementType: ElementType.FILE_TYPE,
            attachment: file,
            content: {
                ...content,
                displayMode,
            },
        });
    };

    const createShortcutLink = async ({ location, currentBoardId, url, error, creationSource }) => {
        const elementId = await createShortcutElement({
            elementType: ElementType.LINK_TYPE,
            location,
            currentBoardId,
            creationSource,
        });

        // load the preview immediately rather than waiting for
        // the user to unfocus the newly-created element
        dispatch(
            setElementLocalData({
                id: elementId,
                data: { url, error, shouldLoadPreview: !error },
            }),
        );

        return elementId;
    };

    const createShortcutTable = ({ location, currentBoardId, values, colWidthsPx, creationSource }) =>
        dispatch((_, getState) => {
            const state = getState();
            const gridSize = getGridSize(state);
            const selectedElements = getSelectedElements(state);
            const locale = userLanguagePreferenceSelector(state);

            const dataArray =
                HotTableClipboardManager.getTableCopiedDataForValues(values) ||
                createTableContentDataFromValues(values, locale);
            const data = convertDataToReduxFormat(applyInitialCellValues(dataArray), locale);

            const nCols = getColumnCount(data);
            const hasValidColWidthsPx = !isEmpty(colWidthsPx) && colWidthsPx.length === nCols;
            const colWidthsGU = hasValidColWidthsPx
                ? colWidthsPx.map((colWidthPx) => Math.round(colWidthPx / gridSize))
                : data[0].map(() => TABLE_DEFAULT_COL_WIDTH);

            const isSingleSelected = length(selectedElements) === 1;
            if (isSingleSelected && isTable(selectedElements.first())) {
                return dispatch(
                    updateSelectedElements({
                        changes: {
                            tableContent: {
                                data,
                                colWidthsGU,
                            },
                            width: colWidthsGU.reduce((sum, val) => sum + val, 0),
                        },
                    }),
                );
            }

            return createShortcutElement({
                elementType: ElementType.TABLE_TYPE,
                location,
                content: {
                    tableContent: {
                        data,
                        colWidthsGU,
                    },
                    width: colWidthsGU.reduce((sum, val) => sum + val, 0),
                },
                currentBoardId,
                creationSource,
            });
        });

    /**
     * This is used to create a better drag and drop experience when dropping images
     * (e.g. from Google image results).
     * It will first create an image element with the specified src (which might be a base64 data URI).
     * Then it will retrieve the link details and overwrite the element with the retrieved data.
     */
    const createShortcutImageLink = ({
        location,
        currentBoardId,
        url,
        src,
        isDataUri,
        width,
        height,
        creationSource,
    }) => {
        const _currentBoardId = currentBoardId || location.parentId;

        const elementId = dispatch(
            createElementSync({
                elementType: ElementType.IMAGE_TYPE,
                location,
                content: {
                    image: {
                        width,
                        height,
                        original: src,
                        isDataUri,
                        transparent: true,
                    },
                    showCaption: true,
                },
                currentBoardId: _currentBoardId,
                creationSource,
            }),
        );

        dispatch(setLinkElementUrl({ id: elementId, url }));
    };

    return {
        deselectAll,
        dispatchGetSelectedElements,
        dispatchGetCurrentlyEditingId,
        dispatchGetPageId,
        dispatchGetCurrentModalElementId,
        dispatchGetClipboardSaveId,
        dispatchSaveToClipBoard,
        dispatchPasteElements,
        createShortcutElement,
        createShortcutCard,
        createShortcutFile,
        createShortcutLink,
        createShortcutTable,
        createShortcutImageLink,
    };
};

export default connect(null, mapDispatchToProps);
